/**
 * Colonists — arrival, food, happiness, population.
 *
 * The first layer beyond the PoC. Spec §1 promised these would "bolt onto the
 * sim module without restructuring it"; this is that claim being tested.
 *
 * MANUFACTURING IS AUTOMATED. Nothing in mining, refining, synthesis or
 * construction reads population. Colonists are not labour, and the entire
 * existing economy runs identically whether the colony holds zero people or a
 * hundred thousand. That decoupling is what lets this be a layer rather than a
 * rewrite: a colonist's goals are to live and to breed.
 *
 * Four mechanics interlock here, and the interlock is the design:
 *
 *   Food spoils, so producing it early is waste.
 *   So buildings need an off switch, so nothing runs before it is wanted.
 *   So the player chooses when the colony comes online.
 *   And expected happiness lets them see, before committing, whether the
 *   colonists they invite will be content.
 */

import {
  ARRIVAL_BATCH,
  BREEDING_HAPPINESS_BP,
  BREEDING_RATE_PER_10K,
  EMIGRATION_HAPPINESS_BP,
  EMIGRATION_RATE_PER_10K,
  FOOD_BUCKETS,
  FOOD_PER_1000_COLONISTS,
  FOOD_SHELF_LIFE_TICKS,
  HAPPINESS_WEIGHT_AMENITY,
  HAPPINESS_WEIGHT_FOOD,
  HAPPINESS_WEIGHT_HOUSING,
  HAPPINESS_WEIGHT_WATER,
} from './constants';
import { BUILDINGS } from './data';
import type { ColonyState, FoodStore, GameState, Happiness } from './types';

const BP = 10000;

/** Ticks each food bucket covers before it ages out. */
export const FOOD_BUCKET_TICKS = Math.floor(FOOD_SHELF_LIFE_TICKS / FOOD_BUCKETS);

export function emptyFoodStore(): FoodStore {
  return { buckets: Array.from({ length: FOOD_BUCKETS }, () => 0), bucketAge: 0 };
}

export function emptyColony(): ColonyState {
  return {
    population: 0,
    food: emptyFoodStore(),
    happinessBp: 0,
    totalArrived: 0,
    totalDeparted: 0,
  };
}

export function totalFood(store: FoodStore): number {
  let total = 0;
  for (const amount of store.buckets) total += amount;
  return total;
}

// --- what the city is currently supplying ------------------------------------

export interface ColonyCapacity {
  livingSpace: number;
  potableWater: number;
  amenity: number;
  /** Food produced per tick by running farms. */
  foodPerTick: number;
}

/**
 * Sum the outputs of buildings that are COMPLETE AND SWITCHED ON.
 *
 * A building that is off contributes nothing. That is the whole mechanic: a farm
 * running before anyone is there to eat spends power to produce food that will
 * spoil before it is wanted.
 */
export function colonyCapacity(state: GameState): ColonyCapacity {
  const capacity: ColonyCapacity = {
    livingSpace: 0,
    potableWater: 0,
    amenity: 0,
    foodPerTick: 0,
  };

  for (const row of state.city.grid) {
    for (const plot of row) {
      if (plot === null || plot.state !== 'complete' || !plot.enabled) continue;
      const output = BUILDINGS[plot.buildingType].output;
      switch (output.kind) {
        case 'livingSpace':
          capacity.livingSpace += output.amount;
          break;
        case 'potableWater':
          capacity.potableWater += output.amount;
          break;
        case 'amenity':
          capacity.amenity += output.amount;
          break;
        case 'food':
          capacity.foodPerTick += output.amount;
          break;
        default:
          break;
      }
    }
  }

  return capacity;
}

/** Food a population eats per tick. */
export function foodDemand(population: number): number {
  return Math.floor((population * FOOD_PER_1000_COLONISTS) / 1000);
}

// --- happiness ---------------------------------------------------------------

function satisfaction(supply: number, demand: number): number {
  if (demand <= 0) return BP;
  return Math.min(BP, Math.floor((supply * BP) / demand));
}

/**
 * Score how well a city of this size is served.
 *
 * Used for BOTH the real population and a hypothetical one. That is deliberate:
 * the number shown to the player before they commit is produced by the same
 * function that judges them afterwards, so the projection cannot flatter.
 *
 * Each component is capped at full satisfaction so a surplus of amenity cannot
 * paper over a famine. Food carries the most weight because it is the need that
 * fails fastest — capacity for housing and water is fixed once built, but food
 * production can stop the moment a farm is switched off or loses power.
 */
export function scoreHappiness(
  capacity: ColonyCapacity,
  population: number,
  foodAvailable: number,
): Happiness {
  if (population <= 0) {
    // Nobody to be unhappy. Report the components as fully met rather than
    // dividing by zero; the projection below is what answers the real question.
    return {
      overallBp: BP,
      foodBp: BP,
      waterBp: BP,
      housingBp: BP,
      amenityBp: BP,
      population: 0,
    };
  }

  const foodBp = satisfaction(foodAvailable, foodDemand(population));
  const waterBp = satisfaction(capacity.potableWater, population);
  const housingBp = satisfaction(capacity.livingSpace, population);
  const amenityBp = satisfaction(capacity.amenity, population);

  const overallBp = Math.floor(
    (foodBp * HAPPINESS_WEIGHT_FOOD +
      waterBp * HAPPINESS_WEIGHT_WATER +
      housingBp * HAPPINESS_WEIGHT_HOUSING +
      amenityBp * HAPPINESS_WEIGHT_AMENITY) /
      BP,
  );

  return { overallBp, foodBp, waterBp, housingBp, amenityBp, population };
}

/**
 * Happiness the colonists WOULD have if the city filled to its housing.
 *
 * This is the question the player actually has before inviting anyone: not "are
 * my zero colonists happy" but "if I bring people, will they be". Projected
 * population is the living space currently switched on, because that is exactly
 * who could arrive.
 *
 * Food is scored on production rate rather than the stockpile, since a stockpile
 * is finite and spoils; what keeps a colony fed is the farms running.
 */
export function projectedHappiness(state: GameState): Happiness {
  const capacity = colonyCapacity(state);
  return scoreHappiness(capacity, capacity.livingSpace, capacity.foodPerTick);
}

// --- food store --------------------------------------------------------------

/**
 * Add this tick's harvest, serve the population from it, then age the store.
 *
 * Order matters. Production lands BEFORE consumption, because food harvested
 * today feeds the colony today — computing demand against yesterday's store
 * meant a colony starved on its arrival tick and began emigrating immediately,
 * however many farms were running.
 *
 * Oldest food is eaten first, which is both what a colony would do and what
 * makes spoilage bite only on genuine surplus.
 *
 * Returns the amount actually eaten.
 */
export function advanceFood(store: FoodStore, produced: number, demand: number): number {
  if (produced > 0) store.buckets[0] = (store.buckets[0] ?? 0) + produced;

  const eaten = Math.min(totalFood(store), Math.max(0, demand));

  // Consume from the oldest bucket forward.
  let remaining = eaten;
  for (let i = store.buckets.length - 1; i >= 0 && remaining > 0; i--) {
    const available = store.buckets[i] ?? 0;
    const taken = Math.min(available, remaining);
    store.buckets[i] = available - taken;
    remaining -= taken;
  }

  store.bucketAge += 1;
  if (store.bucketAge >= FOOD_BUCKET_TICKS) {
    store.bucketAge = 0;
    // Everything shifts one bucket older; whatever falls off the end has spoiled.
    store.buckets.pop();
    store.buckets.unshift(0);
  }

  return eaten;
}

// --- the tick ----------------------------------------------------------------

/**
 * Spec §8 does not number this step: it runs after construction, because a
 * building completed this tick should be available to the colony on the next
 * one, not retroactively.
 */
export function advanceColony(state: GameState): void {
  const capacity = colonyCapacity(state);
  const colony = state.colony;

  const eaten = advanceFood(
    colony.food,
    capacity.foodPerTick,
    foodDemand(colony.population),
  );

  // Scored on what was actually available to eat this tick, not on the harvest
  // alone — a colony living off a shrinking store is not yet unhappy.
  const foodAvailable = colony.population > 0 ? eaten : capacity.foodPerTick;
  const happiness = scoreHappiness(capacity, colony.population, foodAvailable);
  colony.happinessBp = happiness.overallBp;

  if (colony.population <= 0) return;

  if (colony.happinessBp >= BREEDING_HAPPINESS_BP) {
    const room = Math.max(0, capacity.livingSpace - colony.population);
    if (room > 0) {
      // At least one, mirroring emigration. Flooring alone means a colony below
      // 500 people can never grow — 250 x 20/10,000 rounds to zero — so a small
      // content colony would sit frozen forever, which reads as a bug rather
      // than as slow growth.
      const born = Math.min(
        room,
        Math.max(1, Math.floor((colony.population * BREEDING_RATE_PER_10K) / 10000)),
      );
      colony.population += born;
    }
  } else if (colony.happinessBp < EMIGRATION_HAPPINESS_BP) {
    // A failing colony empties out rather than declining imperceptibly. At least
    // one leaves, so a small unhappy population still resolves instead of
    // sticking forever at a number floor rounding cannot move.
    const leaving = Math.max(
      1,
      Math.floor((colony.population * EMIGRATION_RATE_PER_10K) / 10000),
    );
    const departed = Math.min(colony.population, leaving);
    colony.population -= departed;
    colony.totalDeparted += departed;
  }
}

/** Bring a batch of colonists, limited by housing that is switched on. */
export function requestColonists(state: GameState): boolean {
  const capacity = colonyCapacity(state);
  const room = capacity.livingSpace - state.colony.population;
  if (room <= 0) return false;

  const arriving = Math.min(ARRIVAL_BATCH, room);
  state.colony.population += arriving;
  state.colony.totalArrived += arriving;
  return true;
}
